/**
 * Share quote: Capacitor OS sheet on iOS/Android, else Web Share, else clipboard.
 * Native WKWebView often has no navigator.share.
 */

export type ShareQuoteOutcome = 'shared' | 'copied' | 'cancelled';

export type ShareQuotePayload = {
  title: string;
  text: string;
  url?: string;
};

function isShareCancelled(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as { name?: string; message?: string; code?: string };
  const name = String(err.name || '');
  const message = String(err.message || '').toLowerCase();
  const code = String(err.code || '');
  if (name === 'AbortError') return true;
  if (code === '1' || /cancel/i.test(code)) return true;
  return message.includes('cancel') || message.includes('abort');
}

function clipboardBody(payload: ShareQuotePayload): string {
  return payload.url ? `${payload.text}\n${payload.url}` : payload.text;
}

async function shareViaCapacitor(payload: ShareQuotePayload): Promise<boolean> {
  const { Share } = await import('@capacitor/share');
  if (typeof Share?.share !== 'function') return false;
  await Share.share({
    title: payload.title,
    text: payload.text,
    url: payload.url,
    dialogTitle: payload.title,
  });
  return true;
}

async function shareViaWeb(payload: ShareQuotePayload): Promise<boolean> {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return false;
  await navigator.share({
    title: payload.title,
    text: payload.text,
    url: payload.url,
  });
  return true;
}

/** OS share sheet when possible; clipboard if the sheet is unavailable. */
export async function shareQuotePayload(payload: ShareQuotePayload): Promise<ShareQuoteOutcome> {
  const title = String(payload.title || '').trim() || 'Quote';
  const text = String(payload.text || '');
  const next = { title, text, url: payload.url };

  try {
    if (await shareViaCapacitor(next)) return 'shared';
  } catch (error) {
    if (isShareCancelled(error)) return 'cancelled';
  }

  try {
    if (await shareViaWeb(next)) return 'shared';
  } catch (error) {
    if (isShareCancelled(error)) return 'cancelled';
  }

  if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
    throw new Error('Could not share quote');
  }
  await navigator.clipboard.writeText(clipboardBody(next));
  return 'copied';
}
